import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  type PaymentMethod,
  type ProviderBusinessProfileRecord,
  type ReceiptLanguage,
  type ReceiptPaperWidth,
  type ChargeScope,
  type VenueSettingsRecord,
  providerApi,
} from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { useProviderWorkspace } from "./ProviderWorkspaceContext";
import { resolveProviderMembershipRole } from "./provider-workspace-helpers";

type SaveTarget = "business" | "venue" | null;

const paymentMethodOptions: PaymentMethod[] = ["cash", "card", "transfer", "other"];
const receiptLanguageOptions: Array<{ label: string; value: ReceiptLanguage }> = [
  { value: "en", label: "English" },
  { value: "ar", label: "Arabic" },
];
const receiptPaperWidthOptions: Array<{ label: string; value: ReceiptPaperWidth }> = [
  { value: "80mm", label: "80 mm" },
  { value: "58mm", label: "58 mm" },
];
const chargeScopeOptions: Array<{ label: string; value: ChargeScope }> = [
  { value: "food", label: "Food and drinks only" },
  { value: "all", label: "Whole bill (play time and food)" },
];

function emptyToNull(value: string | null) {
  if (value === null) {
    return null;
  }

  const cleaned = value.trim();
  return cleaned.length > 0 ? cleaned : null;
}

function SettingHelp({ children }: { children: ReactNode }) {
  const id = useId();
  const helpRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [position, setPosition] = useState({ left: 8, top: 8 });
  const visible = (open || hovered || focused) && !dismissed;

  const dismiss = () => {
    setOpen(false);
    setDismissed(true);
  };

  useEffect(() => {
    if (!visible) return;

    const handleOutsidePointer = (event: PointerEvent) => {
      if (!helpRef.current?.contains(event.target as Node)) dismiss();
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismiss();
    };

    document.addEventListener("pointerdown", handleOutsidePointer, true);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointer, true);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const tooltipWidth = Math.min(288, window.innerWidth - 16);
      const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - tooltipWidth - 8));
      const top = rect.bottom + 8 + 96 < window.innerHeight || rect.top < 120
        ? rect.bottom + 8
        : rect.top - 104;
      setPosition({ left, top: Math.max(8, top) });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [visible]);

  return (
    <span className="provider-setting-help" ref={helpRef}>
      <button
        aria-controls={id}
        aria-describedby={visible ? id : undefined}
        aria-expanded={visible}
        aria-label="Show setting details"
        className="provider-setting-help-trigger"
        data-open={visible ? "true" : undefined}
        onBlur={() => setFocused(false)}
        onClick={() => {
          if (open) {
            dismiss();
          } else {
            setDismissed(false);
            setOpen(true);
          }
        }}
        onFocus={() => {
          setDismissed(false);
          setFocused(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") dismiss();
        }}
        onMouseEnter={() => {
          setDismissed(false);
          setHovered(true);
        }}
        onMouseLeave={() => setHovered(false)}
        ref={triggerRef}
        type="button"
      >
        <span aria-hidden="true">?</span>
      </button>
      {visible ? createPortal(
        <span
          className="provider-setting-help-popover"
          data-open="true"
          id={id}
          role="tooltip"
          style={{ left: position.left, top: position.top }}
        >
          {children}
        </span>,
        document.body,
      ) : null}
    </span>
  );
}

function SettingLabel({ children, help, htmlFor }: { children: ReactNode; help: string; htmlFor?: string }) {
  return (
    <span className="provider-setting-label">
      {htmlFor ? <label className="ops-label" htmlFor={htmlFor}>{children}</label> : <span className="ops-label">{children}</span>}
      <SettingHelp>{help}</SettingHelp>
    </span>
  );
}

export function SettingsCard() {
  const { selectedVenue, summary, user } = useProviderWorkspace();
  const { t } = useAppPreferences();
  const canManageSettings = resolveProviderMembershipRole(summary, user) === "owner";
  const ownerOnlyHint = canManageSettings
    ? undefined
    : t("Only owners can change these settings.");
  const [businessForm, setBusinessForm] = useState<ProviderBusinessProfileRecord | null>(null);
  const [venueForm, setVenueForm] = useState<VenueSettingsRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<SaveTarget>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Show save feedback next to the form that was saved, not at the top of a long page.
  const [feedbackFor, setFeedbackFor] = useState<SaveTarget>(null);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);
    setNotice(null);

    void Promise.all([
      providerApi.getBusinessProfile(),
      selectedVenue ? providerApi.getVenueSettings(selectedVenue.id) : Promise.resolve(null),
    ])
      .then(([businessProfile, venueSettings]) => {
        if (cancelled) {
          return;
        }

        setBusinessForm(businessProfile);
        setVenueForm(venueSettings ? {
          ...venueSettings,
          receipt_paper_width: venueSettings.receipt_paper_width ?? "80mm",
        } : null);
      })
      .catch(() => {
        if (!cancelled) {
          setError(t("Could not load settings."));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedVenue?.id, t]);

  const updateBusinessField = <Field extends keyof ProviderBusinessProfileRecord>(
    field: Field,
    value: ProviderBusinessProfileRecord[Field],
  ) => {
    setBusinessForm((current) => (current ? { ...current, [field]: value } : current));
  };

  const updateVenueField = <Field extends keyof VenueSettingsRecord>(
    field: Field,
    value: VenueSettingsRecord[Field],
  ) => {
    setVenueForm((current) => (current ? { ...current, [field]: value } : current));
  };


  const togglePaymentMethod = (method: PaymentMethod) => {
    setVenueForm((current) => {
      if (!current) {
        return current;
      }

      const isSelected = current.allowed_payment_methods.includes(method);
      if (isSelected && current.allowed_payment_methods.length === 1) {
        return current;
      }

      return {
        ...current,
        allowed_payment_methods: isSelected
          ? current.allowed_payment_methods.filter((item) => item !== method)
          : [...current.allowed_payment_methods, method],
      };
    });
  };

  const handleSaveBusiness = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!businessForm) {
      return;
    }

    setSaving("business");
    setFeedbackFor("business");
    setError(null);
    setNotice(null);

    try {
      const updated = await providerApi.updateBusinessProfile({
        ...businessForm,
        billing_address: emptyToNull(businessForm.billing_address),
        commercial_registration_number: emptyToNull(businessForm.commercial_registration_number),
        email: emptyToNull(businessForm.email),
        logo_url: emptyToNull(businessForm.logo_url),
        phone: emptyToNull(businessForm.phone),
        tax_registration_number: emptyToNull(businessForm.tax_registration_number),
        trading_name: emptyToNull(businessForm.trading_name),
      });
      setBusinessForm(updated);
      setNotice(t("Business settings saved."));
    } catch {
      setError(t("Could not save business settings."));
    } finally {
      setSaving(null);
    }
  };

  const handleSaveVenue = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedVenue || !venueForm) {
      return;
    }

    if (venueForm.allowed_payment_methods.length === 0) {
      setError(t("Select at least one payment method before saving venue settings."));
      setNotice(null);
      return;
    }

    setSaving("venue");
    setFeedbackFor("venue");
    setError(null);
    setNotice(null);

    try {
      const updated = await providerApi.updateVenueSettings(selectedVenue.id, {
        ...venueForm,
        receipt_footer: emptyToNull(venueForm.receipt_footer),
        receipt_paper_width: venueForm.receipt_paper_width ?? "80mm",
      });
      setVenueForm(updated);
      setNotice(t("Venue finance settings saved."));
    } catch {
      setError(t("Could not save venue finance settings."));
    } finally {
      setSaving(null);
    }
  };


  if (loading && !businessForm) {
    return <div className="ops-empty">{t("Loading settings...")}</div>;
  }

  const feedback = (
    <>
      {error ? <div className="ops-banner" data-tone="danger" role="alert">{error}</div> : null}
      {notice ? <div className="ops-banner" data-tone="success" role="status">{notice}</div> : null}
    </>
  );

  return (
    <div className="grid gap-3">

      {!canManageSettings ? (
        <div className="ops-banner" data-tone="info" role="status">
          {t("Only owners can change these settings.")}
        </div>
      ) : null}

      {feedbackFor === null ? feedback : null}

      {selectedVenue && venueForm ? (
        <form className="ops-surface ops-surface-dense" onSubmit={(event) => void handleSaveVenue(event)}>
          <fieldset className="m-0 grid min-w-0 gap-3 border-0 p-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <p className="ops-kicker">{t("Payments & receipts")}</p>
              <span className="ops-chip" data-tone="info">{selectedVenue.name}</span>
            </div>
            <span title={ownerOnlyHint}>
              <button className="ops-button" data-size="sm" data-variant="primary" disabled={!canManageSettings || saving === "venue"} type="submit">
                {saving === "venue" ? t("Saving...") : t("Save payments & receipts")}
              </button>
            </span>
          </div>
          {feedbackFor === "venue" ? feedback : null}

          <div className="grid gap-3 lg:grid-cols-3">
            <div className="ops-field">
              <SettingLabel htmlFor="provider-setting-receipt-prefix" help={t("Text placed before the running receipt number for this venue.")}>{t("Receipt prefix")}</SettingLabel>
              <input className="ops-input uppercase" disabled={!canManageSettings} id="provider-setting-receipt-prefix" maxLength={16} minLength={1} onChange={(event) => updateVenueField("receipt_prefix", event.target.value.toUpperCase())} value={venueForm.receipt_prefix} />
            </div>
            <div className="ops-field">
              <SettingLabel htmlFor="provider-setting-receipt-language" help={t("The default language used when receipts are printed.")}>{t("Receipt language")}</SettingLabel>
              <select className="ops-select" disabled={!canManageSettings} id="provider-setting-receipt-language" onChange={(event) => updateVenueField("receipt_language_default", event.target.value as ReceiptLanguage)} value={venueForm.receipt_language_default}>
                {receiptLanguageOptions.map((option) => <option key={option.value} value={option.value}>{t(option.label)}</option>)}
              </select>
            </div>
            <div className="ops-field">
              <SettingLabel htmlFor="provider-setting-receipt-paper-width" help={t("Choose the paper roll width used by the receipt printer: 58 mm or 80 mm.")}>{t("Receipt paper width")}</SettingLabel>
              <select className="ops-select" disabled={!canManageSettings} id="provider-setting-receipt-paper-width" onChange={(event) => updateVenueField("receipt_paper_width", event.target.value as ReceiptPaperWidth)} value={venueForm.receipt_paper_width ?? "80mm"}>
                {receiptPaperWidthOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
            <div className="ops-field">
              <SettingLabel htmlFor="provider-setting-vat-percent" help={t("The VAT rate applied when VAT is enabled.")}>{t("VAT %")}</SettingLabel>
              <input className="ops-input" disabled={!canManageSettings || !venueForm.vat_enabled} id="provider-setting-vat-percent" max={100} min={0} onChange={(event) => updateVenueField("vat_percent", Number(event.target.value))} step="0.01" type="number" value={venueForm.vat_percent} />
            </div>
            <div className="ops-field">
              <SettingLabel htmlFor="provider-setting-vat-scope" help={t("Which part of the bill VAT is charged on. Booked play time is never charged food-only VAT.")}>{t("VAT applies to")}</SettingLabel>
              <select className="ops-select" disabled={!canManageSettings || !venueForm.vat_enabled} id="provider-setting-vat-scope" onChange={(event) => updateVenueField("vat_scope", event.target.value as ChargeScope)} value={venueForm.vat_scope ?? "all"}>
                {chargeScopeOptions.map((option) => <option key={option.value} value={option.value}>{t(option.label)}</option>)}
              </select>
            </div>
            <div className="ops-field">
              <SettingLabel htmlFor="provider-setting-service-fee-percent" help={t("The service fee rate applied when service fees are enabled.")}>{t("Service fee %")}</SettingLabel>
              <input className="ops-input" disabled={!canManageSettings || !venueForm.service_fee_enabled} id="provider-setting-service-fee-percent" max={100} min={0} onChange={(event) => updateVenueField("service_fee_percent", Number(event.target.value))} step="0.01" type="number" value={venueForm.service_fee_percent} />
            </div>
            <div className="ops-field">
              <SettingLabel htmlFor="provider-setting-service-fee-scope" help={t("Which part of the bill the service fee is charged on. Booked play time is never charged a food-only service fee.")}>{t("Service fee applies to")}</SettingLabel>
              <select className="ops-select" disabled={!canManageSettings || !venueForm.service_fee_enabled} id="provider-setting-service-fee-scope" onChange={(event) => updateVenueField("service_fee_scope", event.target.value as ChargeScope)} value={venueForm.service_fee_scope ?? "all"}>
                {chargeScopeOptions.map((option) => <option key={option.value} value={option.value}>{t(option.label)}</option>)}
              </select>
            </div>
            <div className="ops-field lg:col-span-2">
              <SettingLabel htmlFor="provider-setting-receipt-footer" help={t("Optional text printed at the bottom of venue receipts.")}>{t("Receipt footer")}</SettingLabel>
              <input className="ops-input" disabled={!canManageSettings} id="provider-setting-receipt-footer" onChange={(event) => updateVenueField("receipt_footer", event.target.value)} placeholder={t("Thanks for playing at {name}", { name: selectedVenue.name })} value={venueForm.receipt_footer ?? ""} />
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <div className="ops-subtle-surface p-2 grid gap-1">
              <SettingLabel help={t("Payment methods available to staff when collecting a receipt balance.")}>{t("Payment methods")}</SettingLabel>
              {paymentMethodOptions.map((method) => {
                const isSelected = venueForm.allowed_payment_methods.includes(method);
                return (
                  <div className="ops-checkbox-row ops-record-dense" key={method}>
                    <input checked={isSelected} disabled={!canManageSettings || (isSelected && venueForm.allowed_payment_methods.length === 1)} id={`provider-setting-payment-${method}`} onChange={() => togglePaymentMethod(method)} type="checkbox" />
                    <label className="text-sm text-slate-200" htmlFor={`provider-setting-payment-${method}`}>{t(method.charAt(0).toUpperCase() + method.slice(1))}</label>
                  </div>
                );
              })}
            </div>

            <div className="ops-subtle-surface p-2 grid gap-1">
              <SettingLabel help={t("Rules that control tax, service fees, partial payments, and unpaid checkout.")}>{t("At checkout")}</SettingLabel>
              <div className="ops-checkbox-row ops-record-dense">
                <input checked={venueForm.vat_enabled} disabled={!canManageSettings} id="provider-setting-vat-enabled" onChange={(event) => updateVenueField("vat_enabled", event.target.checked)} type="checkbox" />
                <label className="text-sm text-slate-200" htmlFor="provider-setting-vat-enabled">{t("Add VAT")}</label>
                <SettingHelp>{t("Apply the venue VAT rate to eligible receipt totals.")}</SettingHelp>
              </div>
              <div className="ops-checkbox-row ops-record-dense">
                <input checked={venueForm.service_fee_enabled} disabled={!canManageSettings} id="provider-setting-service-fee-enabled" onChange={(event) => updateVenueField("service_fee_enabled", event.target.checked)} type="checkbox" />
                <label className="text-sm text-slate-200" htmlFor="provider-setting-service-fee-enabled">{t("Add a service fee")}</label>
                <SettingHelp>{t("Apply the venue service fee rate to eligible checkout totals.")}</SettingHelp>
              </div>
              <div className="ops-checkbox-row ops-record-dense">
                <input checked={venueForm.allow_partial_payments} disabled={!canManageSettings} id="provider-setting-partial-payments" onChange={(event) => updateVenueField("allow_partial_payments", event.target.checked)} type="checkbox" />
                <label className="text-sm text-slate-200" htmlFor="provider-setting-partial-payments">{t("Allow partial payments")}</label>
                <SettingHelp>{t("Let staff collect part of a receipt balance before it is fully paid.")}</SettingHelp>
              </div>
              <div className="ops-checkbox-row ops-record-dense">
                <input checked={venueForm.require_payment_before_session_end} disabled={!canManageSettings} id="provider-setting-payment-before-end" onChange={(event) => updateVenueField("require_payment_before_session_end", event.target.checked)} type="checkbox" />
                <label className="text-sm text-slate-200" htmlFor="provider-setting-payment-before-end">{t("Require payment before ending sessions")}</label>
                <SettingHelp>{t("Prevent staff from ending a session until its balance is collected.")}</SettingHelp>
              </div>
              <div className="ops-checkbox-row ops-record-dense">
                <input checked={venueForm.allow_unpaid_checkout} disabled={!canManageSettings} id="provider-setting-unpaid-checkout" onChange={(event) => updateVenueField("allow_unpaid_checkout", event.target.checked)} type="checkbox" />
                <label className="text-sm text-slate-200" htmlFor="provider-setting-unpaid-checkout">{t("Allow unpaid checkout")}</label>
                <SettingHelp>{t("Allow staff to issue a receipt with an outstanding balance.")}</SettingHelp>
              </div>
            </div>
          </div>
          </fieldset>
        </form>
      ) : (
        <div className="ops-empty">{t("Select a venue to configure receipt and payment settings.")}</div>
      )}


      {businessForm ? (
        <form className="ops-surface ops-surface-dense" onSubmit={(event) => void handleSaveBusiness(event)}>
          <fieldset className="m-0 grid min-w-0 gap-3 border-0 p-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="ops-kicker">{t("Business details")}</p>
            <span title={ownerOnlyHint}>
              <button className="ops-button" data-size="sm" data-variant="primary" disabled={!canManageSettings || saving === "business"} type="submit">
                {saving === "business" ? t("Saving...") : t("Save business details")}
              </button>
            </span>
          </div>
          {feedbackFor === "business" ? feedback : null}
          <div className="grid gap-3 lg:grid-cols-3">
            <div className="ops-field">
              <SettingLabel htmlFor="provider-setting-legal-name" help={t("The official business name used for provider records.")}>{t("Legal name")}</SettingLabel>
              <input className="ops-input" disabled={!canManageSettings} id="provider-setting-legal-name" onChange={(event) => updateBusinessField("legal_name", event.target.value)} required value={businessForm.legal_name} />
            </div>
            <div className="ops-field">
              <SettingLabel htmlFor="provider-setting-trading-name" help={t("The customer-facing name shown when it differs from the legal name.")}>{t("Trading name")}</SettingLabel>
              <input className="ops-input" disabled={!canManageSettings} id="provider-setting-trading-name" onChange={(event) => updateBusinessField("trading_name", event.target.value)} value={businessForm.trading_name ?? ""} />
            </div>
            <div className="ops-field">
              <SettingLabel htmlFor="provider-setting-tax-registration" help={t("Your tax registration identifier for business records.")}>{t("Tax registration")}</SettingLabel>
              <input className="ops-input" disabled={!canManageSettings} id="provider-setting-tax-registration" onChange={(event) => updateBusinessField("tax_registration_number", event.target.value)} value={businessForm.tax_registration_number ?? ""} />
            </div>
            <div className="ops-field">
              <SettingLabel htmlFor="provider-setting-commercial-registration" help={t("Your commercial registration identifier for business records.")}>{t("Commercial registration")}</SettingLabel>
              <input className="ops-input" disabled={!canManageSettings} id="provider-setting-commercial-registration" onChange={(event) => updateBusinessField("commercial_registration_number", event.target.value)} value={businessForm.commercial_registration_number ?? ""} />
            </div>
            <div className="ops-field">
              <SettingLabel htmlFor="provider-setting-business-email" help={t("The main business email used for operational contact.")}>{t("Business email")}</SettingLabel>
              <input className="ops-input" disabled={!canManageSettings} id="provider-setting-business-email" onChange={(event) => updateBusinessField("email", event.target.value)} type="email" value={businessForm.email ?? ""} />
            </div>
            <div className="ops-field">
              <SettingLabel htmlFor="provider-setting-business-phone" help={t("The main phone number customers or staff can use to reach the business.")}>{t("Business phone")}</SettingLabel>
              <input className="ops-input" disabled={!canManageSettings} id="provider-setting-business-phone" onChange={(event) => updateBusinessField("phone", event.target.value)} value={businessForm.phone ?? ""} />
            </div>
            <div className="ops-field lg:col-span-2">
              <SettingLabel htmlFor="provider-setting-billing-address" help={t("The business address used for billing and records.")}>{t("Billing address")}</SettingLabel>
              <input className="ops-input" disabled={!canManageSettings} id="provider-setting-billing-address" onChange={(event) => updateBusinessField("billing_address", event.target.value)} value={businessForm.billing_address ?? ""} />
            </div>
            <div className="ops-field">
              <SettingLabel htmlFor="provider-setting-currency" help={t("The default currency used for business amounts and pricing.")}>{t("Currency")}</SettingLabel>
              <input className="ops-input uppercase" disabled={!canManageSettings} id="provider-setting-currency" maxLength={3} minLength={3} onChange={(event) => updateBusinessField("default_currency", event.target.value.toUpperCase())} value={businessForm.default_currency} />
            </div>
          </div>
          </fieldset>
        </form>
      ) : null}
    </div>
  );
}
